import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import Image from 'next/future/image';
import { useEffect, useState } from 'react';
import {
  CustomerSubscriptionChannel,
  CustomerSubscriptionSource,
  CustomerSubscriptionStatus,
} from '@lambda/apis/src/customer/enum';
import { CreateCognitoCustomer } from '@lambda/apis/src/customer/cognito/types';
import { useRouter } from 'next/router';
import { CtaButton } from '@/components/Cta/Cta';
import settings from '@/settings/configs';
import openEyeIcon from '@/public/icons/account/eye_open.svg';
import closedEyeIcon from '@/public/icons/account/eye_closed.svg';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import {
  trackSignup,
  trackSubscriptionPreferencesUpdated,
} from '@/lib/analytics/trackCustomer';
import { loadGoogleSigninScript } from '@/lib/google-signin';
import { CustomerFormType, SignupForm } from './types';
import { signupFormSchema } from './constants';
import Or from '../Or';
import googleIcon from '@/public/icons/account/google.svg';
import { CognitoRegisterFormProps } from './CognitoLoginForm/types';
import CognitoPassowrdReq from './CognitoPasswordReq';

export default function CognitoRegisterForm(props: CognitoRegisterFormProps) {
  const router = useRouter();
  const form = useForm<SignupForm>({ resolver: yupResolver(signupFormSchema) });
  const [showPassword, setShowPassword] = useState(false);
  const { cognitoSignup, cognitoLogin, loading, updateEmailSubscription } =
    useCustomerContext();
  const [registerError, setRegisterError] = useState('');

  useEffect(() => {
    loadGoogleSigninScript();
  }, []);

  const signupAndCallback = async (input: {
    acceptsMarketing: boolean;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    try {
      const req: CreateCognitoCustomer = {
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        password: input.password,
      };

      const { customer, error, message } = await cognitoSignup(req);

      if (error || !customer) {
        setRegisterError(
          message ?? 'Unable to sign up. Please try again later.',
        );

        return;
      }

      const loginCustomer = await cognitoLogin(input.email, input.password);

      if (!loginCustomer) return;

      const emailSubscriptionStatus = input.acceptsMarketing
        ? CustomerSubscriptionStatus.SUBSCRIBED
        : CustomerSubscriptionStatus.UNSUBSCRIBED;

      trackSignup();

      await updateEmailSubscription({
        customerId: customer.reebeloId,
        status: emailSubscriptionStatus,
        source: CustomerSubscriptionSource.SIGNUP,
      });

      trackSubscriptionPreferencesUpdated(
        input.email,
        emailSubscriptionStatus,
        CustomerSubscriptionChannel.EMAIL,
        CustomerSubscriptionSource.SIGNUP,
      );

      props.onSuccess();
    } catch (err) {
      setRegisterError('Unable to sign up. Please try again later.');
    }
  };

  const signUpWithGoogle = () => {
    window.location.href = '/api/customer/auth/cognito/google';
  };

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Create my account</h2>
      </div>
      <div className="mt-2.5 flex w-full flex-col">
        <input
          className="h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
          placeholder="First name"
          type="text"
          {...form.register('firstName')}
        />
        {form.formState.errors.firstName && (
          <div className="mt-1 text-xs text-red">
            {form.formState.errors.firstName.message}
          </div>
        )}
      </div>
      <div className="flex w-full flex-col">
        <input
          className="mt-2 h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
          placeholder="Last name"
          {...form.register('lastName')}
        />
        {form.formState.errors.lastName && (
          <div className="mt-1 text-xs text-red">
            {form.formState.errors.lastName.message}
          </div>
        )}
      </div>

      <div className="flex w-full flex-col">
        <input
          className="mt-2 h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
          placeholder="Email"
          type="email"
          {...form.register('email')}
        />
        {form.formState.errors.email && (
          <div className="mt-1 text-xs text-red">
            {form.formState.errors.email.message}
          </div>
        )}
      </div>
      <div className="mt-2">
        <div className="relative">
          <input
            className="h-12 w-full rounded border border-gray-700/10 px-3 text-gray-700 outline-1 outline-gray-700 placeholder:text-gray-700/50"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            {...form.register('password')}
          />
          <button
            className="absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <Image
              src={showPassword ? openEyeIcon : closedEyeIcon}
              alt="Toggle Password"
              height={50}
              width={50}
              className="h-full w-full"
            />
          </button>
        </div>
        {form.formState.errors.password && (
          <div className="mt-1 text-xs text-red">
            {form.formState.errors.password.message}
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center text-gray-400 md:text-xs">
        <input
          {...form.register('acceptsMarketing')}
          className="accent-gray-700"
          type="checkbox"
          id="acceptsMarketing"
          name="acceptsMarketing"
          defaultChecked={settings.store !== 'reebelo-ca'}
        />
        <label htmlFor="acceptsMarketing" className="ml-2">
          Subscribe to our newsletter for exclusives & early access.
        </label>
      </div>
      <div className="mt-4 text-gray-400 md:text-xs">
        By signing up, you agree to our{' '}
        <Link href={'/policies/terms-of-service'}>
          <a
            className="cursor-pointer text-[#0D6EFD] underline underline-offset-1"
            onClick={() => props?.onClose && props.onClose()}
          >
            T&Cs
          </a>
        </Link>{' '}
        and{' '}
        <Link href={'/policies/privacy-policy'}>
          <a
            className="cursor-pointer text-[#0D6EFD] underline underline-offset-1"
            onClick={() => props?.onClose && props.onClose()}
          >
            Privacy Policy
          </a>
        </Link>
      </div>
      <CtaButton
        type="submit"
        className="mt-2 !h-12 w-full !rounded !text-sm"
        onClick={form.handleSubmit(signupAndCallback)}
        loading={loading}
      >
        Create my account
      </CtaButton>
      {registerError && (
        <div className="mt-1 text-xs text-red">{registerError}</div>
      )}
      <CognitoPassowrdReq password={form.watch('password')} />
      <Or className="my-2" />
      <button
        type="button"
        className="relative mt-2 flex h-12 w-full items-center justify-center gap-x-2 rounded border border-gray-700 px-5 text-sm font-bold underline-offset-1 hover:underline"
        onClick={() => signUpWithGoogle()}
      >
        <div className="h-5 w-5">
          <Image
            alt="Sign in with Google"
            src={googleIcon}
            height={50}
            width={50}
            className="h-full w-full"
          />
        </div>
        <span className="block">Sign up with Google</span>
      </button>
      <div className="my-4 h-px w-full flex-1 bg-gray-700/10" />
      <div className=" text-center">
        Already a member?{' '}
        <button
          className="text-[#0D6EFD] underline underline-offset-1"
          onClick={() => {
            if (props.setFormType) props.setFormType(CustomerFormType.LOGIN);
            else router.push('/account/login');
          }}
        >
          Log-in here
        </button>
      </div>
    </div>
  );
}
